import { HStack, VStack } from "@chakra-ui/layout";
import { Tag, TagLabel, Text } from "@chakra-ui/react";
import React, { useMemo, useState } from "react";
import ShowMoreHorizontal from "../icons/ShowMoreHorizontal";
import TransparentRoundedClose from "../icons/TransparentRoundedClose";
// @ts-ignore - colord has module resolution issues in strict mode
import { colord } from "colord";
import { appTheme } from "@styles/Theme";

// The unselected tag background - the accent colour at 15%.
const accentSoft = colord(appTheme.colors.accent).alpha(0.15).toRgbString();

export interface TagOption {
	key: string;
	value: string;
	isSelected?: boolean;
}

interface Props {
	tagList: TagOption[];
	onClick?: (tagKey: string) => void;
	label?: string;
	disabled?: boolean;
	removable?: boolean;
	userGuideText?: string;
	borderColor?: string;
	textColor?: string;
	tagsAlignment?: string;
	labelFontWeight?: number;
	guideTextFontWeight?: number;
	tagMargin?: string | number;
	maxVisibleTags?: number;
}

const TagList = ({
	label,
	tagList,
	onClick,
	disabled = false,
	removable = false,
	userGuideText,
	labelFontWeight = 700,
	guideTextFontWeight = 400,
	tagsAlignment = "flex-start",
	tagMargin = 0,
	borderColor,
	textColor,
	maxVisibleTags = -1,
}: Props) => {
	const sortedTagList = tagList.sort((a, b) => a.value.localeCompare(b.value));
	const [uncap, setUncap] = useState(false);

	const cappedTagList = useMemo(() => {
		if (uncap) {
			return sortedTagList;
		}

		return sortedTagList.slice(0, maxVisibleTags);
	}, [sortedTagList, maxVisibleTags, uncap]);

	return (
		<VStack width={312} alignItems="start" mr={5} minWidth={"100%"} spacing={4}>
			{label && (
				<Text fontSize="14px" fontWeight={labelFontWeight} m={0}>
					{label}
				</Text>
			)}
			{userGuideText && (
				<Text fontSize="16px" fontWeight={guideTextFontWeight} m={0}>
					{userGuideText}
				</Text>
			)}
			{removable ? (
				<HStack wrap={"wrap"} align={"center"}>
					{cappedTagList.map((tag) => (
						<Tag
							size={"md"}
							key={tag.key}
							borderRadius="full"
							variant="solid"
							m={2}
							px={2}
							color={"white"}
							bg={"accent.500"}
							userSelect={"none"}
						>
							<TagLabel>{tag.value}</TagLabel>
							<TransparentRoundedClose
								style={{ marginLeft: "5px", cursor: "pointer" }}
								onClick={onClick ? () => onClick(tag.key) : undefined}
							/>
						</Tag>
					))}
				</HStack>
			) : (
				<HStack wrap={"wrap"} justifyContent={tagsAlignment}>
					{!disabled ? (
						cappedTagList.map((tag) => (
							<Tag
								size={"md"}
								key={tag.key}
								borderRadius="full"
								variant="solid"
								borderWidth="1px"
								m={tagMargin}
								px={3}
								borderColor={borderColor ? borderColor : "accent.500"}
								color={textColor ? textColor : tag.isSelected ? "white" : "black"}
								bg={tag.isSelected ? "accent.500" : accentSoft}
								cursor={"pointer"}
								onClick={onClick ? () => onClick(tag.key) : undefined}
								userSelect={"none"}
							>
								<TagLabel>{tag.value}</TagLabel>
							</Tag>
						))
					) : (
						<Tag borderRadius="full" cursor="not-allowed" m={2} px={3}>
							<ShowMoreHorizontal />
						</Tag>
					)}
				</HStack>
			)}

			{cappedTagList.length < sortedTagList.length && (
				<Tag
					size={"md"}
					borderRadius="full"
					variant="solid"
					borderWidth="1px"
					m={tagMargin}
					px={3}
					color={"white"}
					bg={"black"}
					cursor={"pointer"}
					onClick={() => setUncap(true)}
					userSelect={"none"}
					alignSelf={"center"}
				>
					<TagLabel>+{sortedTagList.length - cappedTagList.length} fler</TagLabel>
				</Tag>
			)}
		</VStack>
	);
};

export default TagList;
